import { z } from 'zod';

import { ApiContractError } from './http';

const effect = z.enum(['grant', 'revoke']);
const plane = z.enum(['job', 'admin']);

const crudLeafSchema = z.object({
  key: z.string(),
  verb: z.string(),
});

const actionTierSchema = z.object({
  level: z.string().min(1),
  only: z.boolean(),
});

const actionLeafSchema = z.object({
  key: z.string(),
  label: z.string(),
  legacyAlias: z.string().nullable().default(null),
  adminPlane: z.boolean().default(false),
  fenced: z.boolean().default(false),
  tier: actionTierSchema.nullable().default(null).catch(null),
});

const featureSchema = z.object({
  featureKey: z.string(),
  label: z.string(),
  plane,
  crud: z.array(crudLeafSchema).default([]),
  actions: z.array(actionLeafSchema).default([]),
});

const moduleSchema = z.object({
  moduleKey: z.string(),
  label: z.string(),
  alwaysOn: z.boolean().default(false),
  features: z.array(featureSchema).default([]),
});

const exceptionPermissionSchema = z.union([
  z.string().transform((key) => ({ key, requiresModule: null as string | null })),
  z.object({ key: z.string(), requiresModule: z.string().nullable().default(null) }),
]);

export const treeSchema = z.object({
  modules: z.array(moduleSchema).default([]),
  exceptionPermissions: z.array(exceptionPermissionSchema).default([]),
  fencedKeys: z.array(z.string()).default([]),
});
export type TreeDto = z.infer<typeof treeSchema>;

const overrideSchema = z.object({
  overrideId: z.string(),
  permissionKey: z.string(),
  effect,
  reason: z.string(),
});

const exceptionSchema = z.object({
  grantId: z.string(),
  exceptionKey: z.string(),
  reason: z.string(),
  expiresAt: z.string().nullable().default(null),
  expired: z.boolean().default(false),
});

const plantAccessSchema = z.object({
  plantId: z.string(),
  name: z.string().nullable().default(null),
  assigned: z.boolean().default(false),
  outOfCluster: z.boolean().default(false),
  licensedModules: z.array(z.string()).default([]),
  overrides: z.array(overrideSchema).default([]),
  exceptions: z.array(exceptionSchema).default([]),
  effectiveKeys: z.array(z.string()),
});

const inviteStateSchema = z.object({
  sentAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  expired: z.boolean(),
  canResendAt: z.string().nullable(),
});

export const inviteSentSchema = z.object({ userId: z.string(), sentTo: z.string(), invite: inviteStateSchema });

export const inviteReadSchema = z.object({ invite: inviteStateSchema.nullable().default(null) });

export const personAccessSchema = z.object({
  userId: z.string(),
  name: z.string().default(''),
  email: z.string().default(''),
  title: z.string().nullable().default(null),
  company: z.string().nullable().default(null),
  roleKey: z.string().nullable().default(null),
  roleKeys: z.array(z.string()).default([]),
  adminGrant: z.string().nullable().default(null),
  adminGrantClusterId: z
    .union([z.string(), z.object({ clusterId: z.string(), name: z.string().nullable().default(null) })])
    .nullable()
    .default(null),
  adminGrantClusterName: z.string().nullable().default(null),
  clusterName: z.string().nullable().default(null),
  adminKeys: z.array(z.string()).default([]),
  permissionsVersion: z.number().default(1),
  landing: z.string().nullable().default(null),
  plants: z.array(plantAccessSchema).default([]),
  globalOverrides: z.array(overrideSchema).default([]),
});
export type PersonAccessDto = z.infer<typeof personAccessSchema>;

const personListItemSchema = z.object({
  userId: z.string(),
  name: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  title: z.string().nullable().default(null),
  company: z.string().nullable().default(null),
  roleKey: z.string().nullable().default(null),
  adminGrant: z.string().nullable().default(null),
  clusterId: z.string().nullable().default(null),
  clusterName: z.string().nullable().default(null),
  userStatus: z.string().nullable().default(null),
  plants: z.array(z.object({ plantId: z.string(), name: z.string().nullable().default(null) })).default([]),
  overrideCount: z.number().default(0),
  exceptionCount: z.number().default(0),
});

export const peoplePageSchema = z.object({
  items: z.array(personListItemSchema).default([]),
  total: z.number().default(0),
  page: z.number().default(1),
  limit: z.number().default(25),
});
export type PeoplePageDto = z.infer<typeof peoplePageSchema>;

const auditWindowSchema = z.object({ from: z.string(), to: z.string(), timeZone: z.string().nullable().default(null) });

const auditEntrySchema = z.object({
  auditId: z.string(),
  actor: z.object({
    id: z.string().nullable().default(null),
    name: z.string().nullable().default(null),
    label: z.string().default(''),
  }),
  target: z.object({ id: z.string(), name: z.string().nullable().default(null) }).nullable().default(null),
  plant: z.object({ id: z.string(), name: z.string().nullable().default(null) }).nullable().default(null),
  change: z
    .object({
      moduleKey: z.string().nullable().default(null),
      moduleLabel: z.string().nullable().default(null),
      licensed: z.boolean().nullable().default(null),
      contractRef: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
  changeType: z.string(),
  permissionKey: z.string().nullable().default(null),
  before: z.record(z.unknown()).nullable().default(null),
  after: z.record(z.unknown()).nullable().default(null),
  reason: z.string().default(''),
  createdAt: z.string(),
});

export const auditPageSchema = z.object({
  window: auditWindowSchema,
  items: z.array(auditEntrySchema).default([]),
  total: z.number().default(0),
  nextCursor: z.string().nullable().default(null),
  limit: z.number().default(25),
  changeTypes: z.array(z.string()).default([]),
});
export type AuditPageDto = z.infer<typeof auditPageSchema>;

export const auditPlantsSchema = z.object({
  window: auditWindowSchema,
  total: z.number().default(0),
  plants: z
    .array(
      z.object({
        plantId: z.string(),
        plantName: z.string().nullable().default(null),
        clusterId: z.string().nullable().default(null),
        clusterName: z.string().nullable().default(null),
        count: z.number(),
        lastChangeAt: z.string(),
      }),
    )
    .default([]),
});
export type AuditPlantsDto = z.infer<typeof auditPlantsSchema>;

const licencePlantSchema = z.object({
  plantId: z.string(),
  name: z.string().nullable().default(null),
  licensedModules: z.array(z.string()).default([]),
  unlicensedModules: z.array(z.string()).default([]),
  clusterId: z.string().nullable().default(null),
  clusterName: z.string().nullable().default(null),
  peopleCount: z.number().default(0),
});

export const licenceListSchema = z.object({
  modules: z
    .array(z.object({ moduleKey: z.string(), label: z.string(), alwaysOn: z.boolean().default(false) }))
    .default([]),
  plants: z.array(licencePlantSchema).default([]),
  unseededPlants: z.array(licencePlantSchema).default([]),
  unseededPlantIds: z.array(z.string()).default([]),
});
export type LicenceListDto = z.infer<typeof licenceListSchema>;
export type LicencePlantDto = z.infer<typeof licencePlantSchema>;

export const licencePreviewSchema = z.object({
  plantId: z.string(),
  moduleKey: z.string(),
  direction: z.string().optional(),
  usersAtPlant: z.number().default(0),
  usersAffected: z.number().default(0),
  permissionsInModule: z.number().default(0),
  willRevokeSessions: z.boolean().default(false),
});
export type LicencePreviewDto = z.infer<typeof licencePreviewSchema>;

export const setLicencesResultSchema = z.object({
  bulkOperationId: z.string().optional(),
  contractRef: z.string().nullable().optional(),
  changed: z.boolean(),
  results: z
    .array(
      z.object({
        changed: z.boolean(),
        usersAffected: z.number().default(0),
        sessionsRevoked: z.number().default(0),
      }),
    )
    .default([]),
});
export type SetLicencesResultDto = z.infer<typeof setLicencesResultSchema>;

export const clustersSchema = z.object({
  clusters: z
    .array(
      z.object({
        id: z.string(),
        name: z.string().nullable().default(null),
        description: z.string().nullable().default(null),
        plantCount: z.number().default(0),
      }),
    )
    .default([]),
  scoped: z.boolean().default(false),
});
export type ClustersDto = z.infer<typeof clustersSchema>;

export const seedResultSchema = z.object({
  plantId: z.string(),
  created: z.number().default(0),
  licensedModules: z.array(z.string()).default([]),
});
export type SeedResultDto = z.infer<typeof seedResultSchema>;

export const templatesSchema = z.object({
  templates: z
    .array(
      z.object({
        roleKey: z.string(),
        name: z.string().default(''),
        description: z.string().default(''),
        version: z.number().default(0),
        isSystem: z.boolean().default(true),
        isLocked: z.boolean().default(false),
        total: z.number().default(0),
        holders: z.number().default(0),
      }),
    )
    .default([]),
});
export type TemplatesDto = z.infer<typeof templatesSchema>;

export const permissionTemplatesSchema = z.object({
  roles: z
    .array(z.object({ roleKey: z.string(), keys: z.array(z.string()).default([]) }))
    .default([]),
});
export type PermissionTemplatesDto = z.infer<typeof permissionTemplatesSchema>;

export const grantAdminTreeSchema = z.object({
  modules: z
    .array(
      z.object({
        features: z
          .array(z.object({ actions: z.array(z.object({ key: z.string(), ticked: z.boolean().default(false) })).default([]) }))
          .default([]),
      }),
    )
    .default([]),
  totals: z
    .object({ adminPlane: z.number().default(0), tickedByGrant: z.number().default(0) })
    .default({ adminPlane: 0, tickedByGrant: 0 }),
});
export type GrantAdminTreeDto = z.infer<typeof grantAdminTreeSchema>;

interface MenuItemDto {
  name: string;
  link?: string | null;
  children?: MenuItemDto[];
}
const menuItemSchema: z.ZodType<MenuItemDto> = z.lazy(() =>
  z.object({
    name: z.string(),
    link: z.string().nullable().optional(),
    children: z.array(menuItemSchema).optional(),
  }),
);

export const accessPreviewSchema = z.object({
  plantId: z.string(),
  menu: z.array(menuItemSchema).default([]),
  landing: z.string().nullable().default(null),
});
export type AccessPreviewDto = z.infer<typeof accessPreviewSchema>;
export type { MenuItemDto };

const effectOrNull = z
  .string()
  .nullable()
  .optional()
  .transform((raw) => (raw === 'grant' || raw === 'revoke' ? raw : null));

const appliedChangeSchema = z.union([
  z.object({ permissionKey: z.string(), effect: effectOrNull }),
  z.string().transform((permissionKey) => ({ permissionKey, effect: null })),
]);

const cascadedChangeSchema = z.union([
  z.object({ permissionKey: z.string(), effect: effectOrNull, causedBy: z.string().nullable().default(null) }),
  z.string().transform((permissionKey) => ({ permissionKey, effect: null, causedBy: null })),
]);

const violationSchema = z.object({
  code: z.string().default('UNKNOWN'),
  message: z.string().nullable().default(null),
  keys: z.array(z.string()).default([]),
});

const changeList = <S extends z.ZodTypeAny>(item: S) => z.array(item).default([]).catch([]);

export const overridesDryRunSchema = z.object({
  applied: changeList(appliedChangeSchema),
  cascaded: changeList(cascadedChangeSchema),
  violations: z.array(violationSchema).default([]),
});
export type OverridesDryRunDto = z.infer<typeof overridesDryRunSchema>;

export const writeResultSchema = z.object({
  changed: z.boolean(),
  sessionsRevoked: z.number().default(0),
  forcedReLogin: z.boolean().default(false),
  overridesCleared: z.number().default(0),
  exceptionsRevoked: z.number().default(0),
  message: z.string().nullable().default(null),
  applied: changeList(appliedChangeSchema),
  cascaded: changeList(cascadedChangeSchema),
});
export type WriteResultDto = z.infer<typeof writeResultSchema>;

export const assignableSchema = z.object({
  roles: z.array(z.object({ roleKey: z.string(), name: z.string().default('') })).default([]),
  grants: z.array(z.string()).default([]),
});
export type AssignableDto = z.infer<typeof assignableSchema>;

export const createPersonResultSchema = z.object({
  userId: z.string(),
  invite: z
    .object({
      sentTo: z.string(),
      sentAt: z.string().nullable().default(null),
      expiresAt: z.string().nullable().default(null),
      expired: z.boolean().default(false),
      canResendAt: z.string().nullable().default(null),
    })
    .optional(),
  inviteError: z.string().optional(),
});

export const parseOrThrow = <S extends z.ZodTypeAny>(schema: S, raw: unknown, label: string): z.infer<S> => {
  const result = schema.safeParse(raw);
  if (!result.success) {
    throw new ApiContractError(`Unexpected ${label} response shape`, result.error.issues);
  }
  return result.data;
};
